<script lang="ts">
  import { onMount } from 'svelte';

  import { Button, Checkbox, DataTable, Pagination, Toolbar, ToolbarContent, ToolbarSearch } from 'carbon-components-svelte';

  import {
    SlaSafeDataInventoryServiceIds,
    DataInventoryServiceIds,
    DataInventoryServiceIdToName,
    SlaMarketDayOffsets,
    DEFAULT_SLA_MARKET_DAY_OFFSET,
    getSlaMarketDayOffsetLabel,
  } from '../../../../shared/constants';

  import type { DataTableHeader } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';
  import type { SlaSafeDataInventoryServiceId, SlaTaskAssociation } from '../../../../shared/types/sla-task-types';

  type Feed = {
    id: string;
    name: string;
    awsLakeAvailable?: boolean;
    awsArbitratedPcapsAvailable?: boolean;
    awsRawFeedsAvailable?: boolean;
  };

  type ServiceConfig = {
    id: SlaSafeDataInventoryServiceId;
    key: string;
    name: string;
    availabilityKey: keyof Feed;
  };

  type DataRow = {
    id: string;
    name: string;
    [key: string]: boolean | string | number;
  };

  type PaginationState = {
    currentPage: number;
    pageSize: number;
  };

  export let errors: Record<string, unknown> = {},
    values: Record<string, unknown> = {},
    availableFeeds: Feed[] = [],
    disabled = false,
    setValue: (name: string, value: unknown) => void,
    initialAssociations: SlaTaskAssociation[] = [];

  const SERVICE_AVAILABILITY_KEY = {
    [DataInventoryServiceIds.AWS_LAKE]: 'awsLakeAvailable',
    [DataInventoryServiceIds.AWS_ARBITRATED_PCAPS]: 'awsArbitratedPcapsAvailable',
    [DataInventoryServiceIds.AWS_RAW_FEEDS]: 'awsRawFeedsAvailable',
  } as const;

  const services: ServiceConfig[] = SlaSafeDataInventoryServiceIds.map((id) => ({
    id,
    key: `service_${id}`,
    name: DataInventoryServiceIdToName[id],
    availabilityKey: SERVICE_AVAILABILITY_KEY[id] as keyof Feed,
  }));

  const headers: DataTableHeader[] = [
    { key: 'name', value: 'Feed Name' },
    ...services.map((service) => ({ key: service.key, value: service.name })),
  ];

  const headerLabelByKey: Record<string, string> = services.reduce(
    (labels, service) => ({ ...labels, [service.key]: service.name }),
    {} as Record<string, string>
  );

  function serviceIdFromKey(key: string): SlaSafeDataInventoryServiceId {
    return Number(key.replace('service_', '')) as SlaSafeDataInventoryServiceId;
  }

  const pairKey = (feedId: string, serviceId: SlaSafeDataInventoryServiceId): string => `${feedId}|${serviceId}`;

  const marketDayOffsetOptions = SlaMarketDayOffsets.map((value) => ({ value, label: getSlaMarketDayOffsetLabel(value) }));

  let pagination: PaginationState = { currentPage: 1, pageSize: 5 },
    filteredRowIds: string[] = [],
    viewSelectionsOnly = false;

  function isSelected(feedId: string, serviceId: SlaSafeDataInventoryServiceId): boolean {
    return associationByPair.has(pairKey(feedId, serviceId));
  }

  function hasSelections(feedId: string): boolean {
    return associations.some((association) => association.feedId === feedId);
  }

  function toggleRowsView() {
    viewSelectionsOnly = !viewSelectionsOnly;
    pagination = { ...pagination, currentPage: 1 };
  }

  function toggle(feedId: string, serviceId: SlaSafeDataInventoryServiceId) {
    const next: SlaTaskAssociation[] = isSelected(feedId, serviceId)
      ? associations.filter((association) => !(association.feedId === feedId && association.serviceId === serviceId))
      : [...associations, { feedId, serviceId, marketDayOffset: DEFAULT_SLA_MARKET_DAY_OFFSET }];

    setValue('associations', next);
  }

  function setOffset(feedId: string, serviceId: SlaSafeDataInventoryServiceId, marketDayOffset: number) {
    const next = associations.map((association) =>
      association.feedId === feedId && association.serviceId === serviceId ? { ...association, marketDayOffset } : association
    );

    setValue('associations', next);
  }

  onMount(() => {
    if (!Array.isArray(values.associations) && Array.isArray(initialAssociations) && initialAssociations.length) {
      setValue('associations', initialAssociations);
    }
  });

  $: associations = (Array.isArray(values.associations) ? values.associations : []) as SlaTaskAssociation[];
  $: associationByPair = new Map(associations.map((association) => [pairKey(association.feedId, association.serviceId), association]));
  $: allRows = availableFeeds.map((feed) => {
    const row: DataRow = { id: feed.id, name: feed.name };

    services.forEach((service) => {
      const association = associationByPair.get(pairKey(feed.id, service.id));
      row[service.key] = Boolean(association);
      row[`${service.key}_available`] = Boolean(feed[service.availabilityKey]);
      row[`${service.key}_offset`] = association?.marketDayOffset ?? DEFAULT_SLA_MARKET_DAY_OFFSET;
    });

    return row;
  });
  $: rows = viewSelectionsOnly ? allRows.filter((row) => hasSelections(row.id)) : allRows;
  $: totalItems = filteredRowIds.length || rows.length;
</script>

<div class="associations">
  <span class="bx--label required">Associations</span>
  {#if availableFeeds.length === 0}
    <p class="empty">No feeds available.</p>
  {:else}
    <DataTable sortable size="short" {headers} {rows} pageSize={pagination.pageSize} page={pagination.currentPage}>
      <span slot="cell" let:cell let:row>
        {#if cell.key === 'name'}
          {cell.value}
        {:else}
          <div class="service-cell">
            <Checkbox
              hideLabel
              labelText={`${row.name} — ${headerLabelByKey[cell.key]}`}
              checked={cell.value}
              disabled={disabled || !row[`${cell.key}_available`]}
              on:check={() => toggle(row.id, serviceIdFromKey(cell.key))}
            />
            {#if cell.value}
              <select
                class="offset-select"
                name={`offset-${row.id}-${cell.key}`}
                aria-label={`Market day for ${row.name} — ${headerLabelByKey[cell.key]}`}
                {disabled}
                value={String(row[`${cell.key}_offset`])}
                on:change={(event) => setOffset(row.id, serviceIdFromKey(cell.key), Number(event.currentTarget.value))}
              >
                {#each marketDayOffsetOptions as option}
                  <option value={String(option.value)}>{option.label}</option>
                {/each}
              </select>
            {/if}
          </div>
        {/if}
      </span>
      <Toolbar size="sm">
        <ToolbarContent>
          <ToolbarSearch persistent shouldFilterRows placeholder="Search feeds" bind:filteredRowIds />
          <Button kind="ghost" size="small" on:click={toggleRowsView}>
            {viewSelectionsOnly ? 'View All' : 'View Selections Only'}
          </Button>
        </ToolbarContent>
      </Toolbar>
    </DataTable>
    {#if totalItems > pagination.pageSize}
      <Pagination bind:page={pagination.currentPage} bind:pageSize={pagination.pageSize} {totalItems} pageSizes={[5, 10]} />
    {/if}
    {#if errors.associations}
      <p class="error">{errors.associations}</p>
    {/if}
  {/if}
</div>

<style>
  .associations {
    display: flex;
    flex-direction: column;
  }

  .associations :global(.bx--pagination .bx--select-input) {
    background-color: unset;
  }

  .service-cell {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .service-cell :global(.bx--checkbox-wrapper) {
    flex-grow: 0;
  }

  .offset-select {
    max-width: 9rem;
    padding: 0.125rem 0.25rem;
    font-size: 0.75rem;
    color: var(--cds-text-01);
    background-color: var(--cds-field-01, #f4f4f4);
    border: none;
  }

  .required::before {
    content: '* ';
    color: var(--cds-danger-01);
  }

  .error {
    margin: 0;
    font-size: 0.75rem;
    color: var(--cds-text-error, #da1e28);
  }

  .empty {
    margin: 0;
    color: var(--cds-text-secondary, var(--cds-text-03));
  }
</style>
